"use client";

import React, { useState, useEffect } from "react";
import { cn, tinaField, dedupeResourceEdges } from "@/utils";
import Fuse from "fuse.js";

import {
  useQueryState,
  parseAsArrayOf,
  parseAsString,
  parseAsInteger,
} from "nuqs";

import Section from "@/components/ui/section";
import {
  Card,
  CardContent,
  CardDate,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import dayjs from "dayjs";
import { Input } from "@/components/ui/input";
import {
  ArrowUpRightIcon,
  Search,
  X,
  SearchXIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Video from "@/components/Video";
import { TaxonomyBadge } from "@/components/ui/badge";
import Image from "next/image";
import Link from "next/link";
import { RESOURCE_TYPES, type ResourceType } from "@/lib/resource-types";
import { useParams, usePathname } from "next/navigation";

import {
  Pagination as PaginationWrapper,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
  EmptyMedia,
} from "@/components/ui/empty";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { DarkLightToggle } from "@/components/DarkLightToggle";
import { MarkdownInline } from "@/components/MarkdownContent";
import { TaxonomyFilter } from "@/components/TaxonomyFilter";

const VIEW_MODE_STORAGE_KEY = "resources-view-mode";
const PAGE_SIZE = 30;

export default function Resources({
  resources,
  categories,
  tags: _tags = [],
  showTypeFilters = true,
  showCategoryFilters,
  title,
  customFilters = () => null,
  wrapperClassName = "",
}: {
  resources: any;
  categories: any;
  tags?: any;
  showTypeFilters?: boolean;
  showCategoryFilters?: boolean;
  title?: string;
  customFilters?: () => React.ReactNode;
  wrapperClassName?: string;
}) {
  // Always start with "grid" to avoid hydration mismatch
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const displayCategoryFilters = showCategoryFilters ?? showTypeFilters;

  // Pagination state
  const [currentPage, setCurrentPage] = useQueryState(
    "page",
    parseAsInteger.withDefault(1).withOptions({
      shallow: false,
      history: "replace",
    })
  );

  // Load from localStorage after mount (client-side only)
  useEffect(() => {
    const savedMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (savedMode === "list" || savedMode === "grid") {
      setViewMode(savedMode as "grid" | "list");
    }
  }, []);

  // Save to localStorage whenever viewMode changes
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // Get filter params from URL
  const [searchParam, setSearchParam] = useQueryState(
    "search",
    parseAsString.withDefault("").withOptions({
      shallow: true,
      history: "replace",
      throttleMs: 1000,
    })
  );
  const [resourceTypesParam, setResourceTypesParam] = useQueryState(
    "type",
    parseAsArrayOf(parseAsString).withDefault(["all"]).withOptions({
      shallow: true,
      history: "replace",
    })
  );
  const [categoryParam, setCategoryParam] = useQueryState(
    "category",
    parseAsArrayOf(parseAsString).withDefault([]).withOptions({
      shallow: true,
      history: "replace",
    })
  );
  const [tagParam] = useQueryState(
    "tag",
    parseAsArrayOf(parseAsString).withDefault([]).withOptions({
      shallow: true,
      history: "replace",
    })
  );

  // Client-side filtering using useMemo
  const resourcesBeforeCategory = React.useMemo(() => {
    // Unpublished items reach this list only when the server rendered in Draft
    // Mode, so no publish filter here.
    let filtered = dedupeResourceEdges([...resources]);

    // Apply search filter with fuzzy search
    if (searchParam) {
      // Configure Fuse.js for fuzzy searching
      const fuse = new Fuse(filtered, {
        keys: [
          { name: "node.advisoryId", weight: 0.4 },
          { name: "node.cveId", weight: 0.4 },
          { name: "node.title", weight: 0.5 },
          { name: "node.description", weight: 0.2 },
        ],
        threshold: 0.3, // Lower = more strict, higher = more flexible (0.0 exact, 1.0 matches anything)
        distance: 100, // Maximum distance characters can be from expected position
        minMatchCharLength: 2, // Minimum character length to be considered a match
        ignoreLocation: true, // Ignore location of match in string
        includeScore: true,
      });

      const searchResults = fuse.search(searchParam);
      filtered = searchResults.map((result) => result.item);
    }

    // Apply type filter
    if (resourceTypesParam && !resourceTypesParam.includes("all")) {
      filtered = filtered.filter((edge) =>
        resourceTypesParam.includes(edge.node.templateType)
      );
    }

    return filtered;
  }, [resources, searchParam, resourceTypesParam]);

  const categoryCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    for (const edge of resourcesBeforeCategory) {
      const cats = edge.node?.categories;
      if (!cats) continue;
      for (const cat of cats) {
        const slug = cat.category?.categorySlug;
        if (!slug) continue;
        counts[slug] = (counts[slug] || 0) + 1;
      }
    }
    return counts;
  }, [resourcesBeforeCategory]);

  const filteredResources = React.useMemo(() => {
    let filtered = resourcesBeforeCategory;
    if (categoryParam && categoryParam.length > 0) {
      filtered = filtered.filter((edge) => {
        const resource = edge.node;
        return resource.categories?.some((cat) =>
          categoryParam.includes(cat.category?.categorySlug)
        );
      });
    }
    if (tagParam && tagParam.length > 0) {
      filtered = filtered.filter((edge) => {
        const resource = edge.node;
        return resource.tags?.some((item) =>
          tagParam.includes(item.tag?.tagSlug)
        );
      });
    }
    return filtered;
  }, [resourcesBeforeCategory, categoryParam, tagParam]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, resourceTypesParam, categoryParam, tagParam]);

  const resourceType = useParams()?.resourceType || "all";

  const resourceTypeDetails = RESOURCE_TYPES.find(
    (type) => type.value === resourceType || type.indexUrl === resourceType
  );

  const sectionTitle = title
    ? title
    : resourceType !== "all"
      ? `All ${resourceTypeDetails?.labels?.plural ||
      resourceTypeDetails?.labels?.singular
      }`
      : "All Resources";

  const clearFilters = () => {
    setSearchParam(null);
    setResourceTypesParam(null);
    setCategoryParam(null);
  };

  // Calculate pagination on filtered results
  const totalPages = Math.ceil(filteredResources.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const paginatedResources = filteredResources.slice(startIndex, endIndex);

  // const [isDarkMode, setIsDarkMode] = useState(true);
  // const toggleMode = () => {
  //   setIsDarkMode(!isDarkMode);
  // };

  return (
    <Section
      wrapperClassName={cn(
        "!pb-24 lg:!pb-48 light:!bg-white light-wrapper",
        wrapperClassName
      )}
      className="space-y-8 md:space-y-16"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4 lg:gap-y-6 lg:gap-x-4 items-baseline lg:items-center">
        <div className="h3">{sectionTitle}</div>
        <div className="flex items-stretch md:items-center gap-4 flex-col-reverse md:flex-row md:justify-end md:col-span-2">
          <SearchInput
            // className="lg:col-span-2 max-sm:order-2"
            searchParam={searchParam}
            setSearchParam={setSearchParam}
          />
          {displayCategoryFilters && !showTypeFilters && (
            <TaxonomyFilter
              className="!ml-0"
              items={categories}
              selected={categoryParam}
              setSelected={setCategoryParam}
              counts={categoryCounts}
              slugKey="categorySlug"
              nounPlural="Categories"
              nounSingular="category"
            />
          )}
          <div className="flex items-center gap-2">
            <DarkLightToggle />
            <ViewModeToggle setViewMode={setViewMode} viewMode={viewMode} />
          </div>
        </div>

        {showTypeFilters && (
          <ResourcesFilter
            // className="col-span-2 md:col-span-4 max-sm:order-4"
            className="md:col-span-2 mt-0.5"
            resourceTypesParam={resourceTypesParam}
            setResourceTypesParam={setResourceTypesParam}
          />
        )}
        {displayCategoryFilters && showTypeFilters && (
          <TaxonomyFilter
            items={categories}
            selected={categoryParam}
            setSelected={setCategoryParam}
            counts={categoryCounts}
            slugKey="categorySlug"
            nounPlural="Categories"
            nounSingular="category"
          />
        )}
        {/* {customFilters && customFilters()} */}
      </div>

      <div
        className={cn("grid minn-h-[400px]", {
          "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6":
            viewMode === "grid",
          "border-t": viewMode === "list",
        })}
      >
        {paginatedResources.map((edge, index) => {
          const resource = edge.node;
          if (!resource) return null;

          // switch to render the correct item
          switch (resource.templateType) {
            case "advisory":
              return (
                <ResourceItem
                  key={resource.id ?? index}
                  resource={resource}
                  viewMode={viewMode}
                />
              );
            case "article":
            case "blog":
              return (
                <ResourceItem
                  key={resource.id ?? index}
                  resource={resource}
                  viewMode={viewMode}
                />
              );
            case "study":
              return (
                <ResourceItem
                  key={resource.id ?? index}
                  resource={{ ...resource, logo: resource?.header?.logo }}
                  logoTitle={true}
                  viewMode={viewMode}
                />
              );
            case "news":
              return (
                <ResourceItem
                  key={resource.id ?? index}
                  resource={resource}
                  viewMode={viewMode}
                />
              );
            case "press":
              return (
                <ResourceItem
                  key={resource.id ?? index}
                  resource={resource}
                  viewMode={viewMode}
                />
              );
            case "patent":
              return (
                <ResourceItem
                  key={resource.id ?? index}
                  resource={resource}
                  viewMode={viewMode}
                />
              );
            case "report":
              return (
                <ResourceItem
                  key={resource.id ?? index}
                  resource={{
                    ...resource,
                    externalUrl:
                      resource?.uploadReport || resource?.externalUrl || "",
                  }}
                  viewMode={viewMode}
                  ctaLabel="Download Report"
                />
              );
            case "tool":
              return (
                <ResourceItem
                  key={resource.id ?? index}
                  resource={resource}
                  viewMode={viewMode}
                  ctaLabel={resource.hasDetailPage ? "Read more" : "Try it"}
                  logoTitle
                />
              );
            case "video":
              return (
                <Video
                  key={resource.id ?? index}
                  backgroundVariant={viewMode === "grid" ? "light" : "default"}
                  video={{
                    ...resource,
                    description:
                      viewMode === "grid" ? "" : resource.description,
                  }}
                  variant={viewMode === "grid" ? "card" : "list-item"}
                  size={viewMode === "grid" ? "sm" : "md"}
                />
              );
          }

          return null;
        })}

        {filteredResources.length === 0 && resources.length > 0 && (
          <Empty className="col-span-full border rounded-none">
            <EmptyHeader>
              <EmptyMedia variant="icon" className="bg-white/20">
                <Search />
              </EmptyMedia>
              <EmptyTitle>No Results Found</EmptyTitle>
              <EmptyDescription>
                No results found for your search. Try adjusting your filters
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  clearFilters();
                }}
              >
                <SearchXIcon />
                Clear Filters
              </Button>
            </EmptyContent>
          </Empty>
        )}
        {resources.length === 0 && filteredResources.length === 0 && (
          <Empty className="col-span-full border rounded-none">
            <EmptyHeader>
              <EmptyMedia variant="icon" className="bg-white/20">
                <Search />
              </EmptyMedia>
              <EmptyTitle>No Content Added Yet</EmptyTitle>
              <EmptyDescription>
                No content has been added yet. Check back again soon.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={filteredResources.length}
          onPageChange={setCurrentPage}
          pageSize={PAGE_SIZE}
        />
      )}
    </Section>
  );
}

const SearchInput = ({
  className,
  searchParam,
  setSearchParam,
}: {
  className?: string;
  searchParam: string;
  setSearchParam: (value: string | null) => Promise<URLSearchParams>;
}) => {
  const handleSearchChange = (value: string | null) => {
    setSearchParam(value);
  };

  return (
    <div className={cn("flex justify-end items-center ", className)}>
      <div className="relative w-full md:w-[260px] col-span-2">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground opacity-70" aria-hidden />
        <label htmlFor="resources-search-input" className="sr-only">
          Search resources
        </label>
        <Input
          id="resources-search-input"
          placeholder="Search..."
          className="!pl-10 !bg-gray-800 border border-gray-700 text-white placeholder:text-gray-400 !h-11 light:!text-black light:!placeholder:text-gray-600 light:!bg-gray-100/20 light:!border"
          value={searchParam}
          onChange={(e) => handleSearchChange(e.target.value || null)}
        />
        {searchParam && (
          <button
            type="button"
            onClick={() => handleSearchChange(null)}
            className="absolute right-3 top-1/2 -translate-y-1/2 hover:bg-gray-700 rounded p-1 transition-colors"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
};

const ViewModeToggle = ({ viewMode, setViewMode }) => {
  return (
    <div className="flex items-center justify-end gap-1 max-sm:order-1">
      <Button
        variant="link-underline"
        onClick={() => setViewMode("list")}
        className={cn("!px-1 py-2 !h-auto", {
          "opacity-100": viewMode === "list",
          "opacity-90 decoration-transparent hover:opacity-100":
            viewMode !== "list",
        })}
      >
        List View
      </Button>
      <div>|</div>
      <Button
        variant="link-underline"
        onClick={() => setViewMode("grid")}
        className={cn("!px-1 py-2 !h-auto", {
          "opacity-100": viewMode === "grid",
          "opacity-90 decoration-transparent hover:opacity-100":
            viewMode !== "grid",
        })}
      >
        Grid View
      </Button>
    </div>
  );
};

const ResourcesFilter = ({
  className,
  resourceTypesParam,
  setResourceTypesParam,
}: {
  className?: string;
  resourceTypesParam: string[];
  setResourceTypesParam: (value: string[] | null) => Promise<URLSearchParams>;
}) => {
  const selectedFilters = resourceTypesParam;

  const handleFilterToggle = (resourceType: ResourceType) => {
    let newFilters: ResourceType[];

    if (resourceType === "all") {
      newFilters = ["all"];
    } else {
      const withoutAll = selectedFilters.filter((f) => f !== "all");

      if (withoutAll.includes(resourceType)) {
        newFilters = withoutAll.filter(
          (f) => f !== resourceType
        ) as ResourceType[];
        if (newFilters.length === 0) newFilters = ["all"];
      } else {
        newFilters = [...withoutAll, resourceType] as ResourceType[];
      }
    }

    // Update the type param using nuqs - set to null to remove from URL when "all"
    setResourceTypesParam(newFilters.includes("all") ? null : newFilters);
  };

  return (
    <div
      className={cn(
        "flex sm:flex-wrap gap-2 md:gap-3 max-md:overflow-auto max-w-[800px]",
        className
      )}
    >
      {Object.values([
        {
          label: "All",
          value: "all",
        },
        ...RESOURCE_TYPES,
      ]).map((resourceType: any) => (
        <Button
          key={resourceType.value}
          variant="outline"
          onClick={() => handleFilterToggle(resourceType.value as ResourceType)}
          size="sm"
          className={cn("!py-1 min-w-[60px]", {
            "ring-2 ring-inset ring-secondary-400": selectedFilters.includes(
              resourceType.value
            ),
          })}
        >
          {resourceType.label ||
            resourceType?.labels?.plural ||
            resourceType.labels?.singular}
        </Button>
      ))}
    </div>
  );
};



const getResourceUrl = (resource: any): string => {
  const slug = resource._sys?.filename?.replace(".mdx", "") || resource.slug;

  if (resource.templateType === "tool" && resource.hasDetailPage) {
    return `/tools/${slug}`;
  }

  if (resource.externalUrl) {
    return resource.externalUrl;
  }

  // Map template types to their URL paths
  const templateType = resource.templateType;
  const typeConfig = RESOURCE_TYPES.find((type) => type.value === templateType);

  if (!typeConfig) {
    return `/${slug}`;
  }

  // For the 4 resource types we're implementing
  if (["advisory", "article", "blog", "study", "news", "patent"].includes(templateType)) {
    return `/${typeConfig.indexUrl}/${slug}`;
  }

  // For other types that have their own top-level routes
  if (templateType === "report") {
    // return `/reports/${slug}`;
    return "";
  }
  if (templateType === "press") {
    // return `/press/${slug}`;
    return "";
  }
  if (templateType === "video") {
    return `/videos/${slug}`;
  }
  if (templateType === "tool") {
    return "";
  }

  // Fallback
  return `/${slug}`;
};

export const ResourceItem = ({
  resource,
  viewMode = "grid",
  active = false,
  className,
  animateOnHover = true,
  logoTitle = false,
  logoPlusTitle = false,
  ctaLabel = "Read more",
}: {
  resource: any;
  viewMode?: string;
  active?: boolean;
  className?: string;
  animateOnHover?: boolean;
  ctaLabel?: string;
  logoTitle?: boolean;
  logoPlusTitle?: boolean;
}) => {
  const href = getResourceUrl(resource);
  const isDraft = resource.published === false;

  // get url from nextjs
  const url = usePathname();
  const isAllResources =
    url === "/resources" ||
    url === "/research/resources" ||
    url === "/research/resources" ||
    url?.includes("/categories/") ||
    url?.includes("/reports/");

  const showDate = logoTitle ? isAllResources : true;
  const showResourceLabel = showDate && isAllResources;
  const showLogo = (logoTitle && !isAllResources) || logoPlusTitle;
  const isCarousel = viewMode === "carousel";
  const headerBackgroundImage =
    typeof resource?.headerBackgroundImage === "string"
      ? resource.headerBackgroundImage.trim()
      : "";
  const showCarouselBackground = isCarousel && headerBackgroundImage;

  return (
    <Link
      href={href || "#"}
      target={resource.externalUrl && !resource.hasDetailPage ? "_blank" : "_self"}
      key={resource.id}
    >
      <Card
        backgroundVariant={viewMode === "grid" ? "light" : "default"}
        background={
          showCarouselBackground ? (
            <>
              <div
                className="absolute inset-0 z-0 pointer-events-none"
                aria-hidden="true"
              >
                <Image
                  src={headerBackgroundImage}
                  alt=""
                  fill
                  className={cn(
                    "object-cover object-right transition-opacity duration-300",
                    !active && "opacity-100"
                  )}
                  sizes="(max-width: 768px) 90vw, 50vw"
                />
              </div>
              <div
                className={cn(
                  "absolute inset-0 z-[1] pointer-events-none transition-all duration-300",
                  active
                    ? "bg-gradient-to-r from-background/85 via-background/45 to-transparent"
                    : "bg-background/70"
                )}
                aria-hidden="true"
              />
            </>
          ) : undefined
        }
        wrapperClassName={cn("shadow-none transition-colors duration-300", {
          "border-x-0 border-t-0 transition-colors duration-300 light:bg-transparent":
            viewMode === "list",
          "bg-secondary-800 text-white": active && !isCarousel,
          "opacity-60": isDraft,
          "light:!bg-gray-200": showLogo,
          "!bg-transparent": showCarouselBackground,
          "!py-0": showCarouselBackground,
          "opacity-100": isCarousel && !active && !showCarouselBackground,
        })}
        className={cn("h-full group flex flex-col transition-colors duration-300", className, {
          "lg:min-h-[352px]": viewMode === "grid" || isCarousel,
          "lg:min-h-[320px]": viewMode === "grid" && showLogo,
          "bg-secondary-800 text-white": active && !isCarousel,
          "!bg-transparent": showCarouselBackground,
        })}
        animateOnHover={viewMode === "grid" && animateOnHover}
      >
        {isDraft && (
          <div className="absolute top-0 right-0 z-10 w-full h-full pointer-events-none flex items-center justify-center">
            <div className="text-8xl text-white/20 font-medium w-full text-center -rotate-45">
              Draft
            </div>
          </div>
        )}

        <CardContent
          className={cn("flex-1 min-h-0 gap-2 grid", {
            "gap-3 gap-x-8 grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto_minmax(0,1fr)_auto] grid-area z-10 relative":
              viewMode === "grid" || isCarousel,
            "py-4 md:py-6": showCarouselBackground,
            "gap-2 gap-x-16 grid-cols-[minmax(0,1fr)_auto_auto] grid-rows-[auto_auto_minmax(0,1fr)_auto] !px-0":
              viewMode === "list",
            "grid-rows-[minmax(50px,1fr)_auto_auto_auto] md:grid-rows-[minmax(70px,1fr)_auto_auto_auto] grid-area":
              viewMode === "grid" && showLogo,
          })}
          style={{
            ...(viewMode === "grid" || viewMode === "carousel"
              ? {
                gridTemplateAreas: `
                    "date type"
                    "title title"
                    "desc desc"
                    "categories cta"
                  `,
              }
              : {
                gridTemplateAreas: `
                    "date . type"
                    "title title cta"
                    "desc desc cta"
                    "categories categories cta"
                  `,
              }),
          }}
        >
          {showResourceLabel && (
            <ResourceLabel resource={resource} style={{ gridArea: "type" }} />
          )}
          {!showLogo && (
            <Date
              resource={resource}
              active={active}
              viewMode={viewMode}
              style={{ gridArea: "date" }}
            />
          )}
          {showLogo ? (
            <>
              <Logo resource={resource} style={{ gridArea: "date" }} />
              {logoPlusTitle && (
                <Title
                  resource={resource}
                  viewMode={viewMode}
                  className="mt-4"
                  style={{ gridArea: "title" }}
                  size={
                    active ||
                      (resource?.templateType === "tool" && !isAllResources)
                      ? "lg"
                      : viewMode === "grid" || viewMode === "carousel"
                        ? "sm"
                        : "md"
                  }
                />
              )}
            </>
          ) : (
            <Title
              resource={resource}
              viewMode={viewMode}
              style={{ gridArea: "title" }}
              size={
                viewMode === "carousel" && active
                  ? "lg"
                  : viewMode === "grid"
                    ? "sm"
                    : "md"
              }
            />
          )}

          <Description
            resource={resource}
            viewMode={viewMode}
            size={viewMode === "grid" || viewMode === "carousel" ? "sm" : "md"}
            style={{ gridArea: "desc" }}
          />
          <Categories
            resource={resource}
            compact={viewMode === "grid" || isCarousel}
            reserveStack={isCarousel}
            style={{ gridArea: "categories" }}
          />
          {ctaLabel && (
            <CTA
              resource={resource}
              ctaLabel={ctaLabel}
              compact={viewMode === "grid" || isCarousel}
              style={{ gridArea: "cta" }}
            />
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

function ResourceLabel({
  resource,
  ...props
}: { resource: any } & React.ComponentProps<any>) {
  return (
    <div
      className="font-mono text-[13px] text-muted-foreground-lighter uppercase text-right"
      {...props}
    >
      {RESOURCE_TYPES.find((type) => type.value === resource.templateType)
        ?.labels?.singular || resource.templateType}
    </div>
  );
}

function Date({
  resource,
  active = false,
  viewMode = "grid",
  ...props
}: {
  resource: any;
  active?: boolean;
  viewMode?: string;
} & React.ComponentProps<typeof CardDate>) {
  if (!resource?.publishDate) {
    return null;
  }
  return (
    <CardDate
      className={cn("mb-4", {
        "mb-2": viewMode === "list",
        "!text-white": active && viewMode !== "carousel",
      })}
      data-tina-field={tinaField(resource, "publishDate")}
      {...props}
    >
      {dayjs(resource?.publishDate).format("MMM D, YYYY")}
    </CardDate>
  );
}

function Logo({
  resource,
  src: propSrc,
  alt: propAlt,
  ...props
}: { resource: any } & React.ComponentProps<any>) {
  const fromResource =
    typeof resource?.logo === "string" ? resource.logo.trim() : "";
  const fromProp =
    typeof propSrc === "string" && propSrc.trim() !== ""
      ? propSrc.trim()
      : "";
  const imageSrc = fromProp || fromResource ? fromProp || fromResource : null;
  const altText =
    typeof propAlt === "string" && propAlt.trim() !== ""
      ? propAlt.trim()
      : typeof resource?.title === "string"
        ? resource.title
        : "Resource logo";

  return (
    <div
      className="py-2 flex"
      {...props}
      data-tina-field={tinaField(resource, "logo")}
    >
      {imageSrc ? (
        <Image
          src={imageSrc}
          alt={altText}
          width={100}
          height={100}
          sizes="(max-width: 768px) 40vw, 200px"
          className={cn("w-auto h-auto max-w-[160px] max-h-8 object-contain", {
            "!max-w-[200px] !max-h-20": resource?.templateType === "tool",
          })}
        />
      ) : null}
    </div>
  );
}

function Title({
  resource,
  viewMode,
  style,
  className,
  ...props
}: { resource: any; viewMode: string } & React.ComponentProps<
  typeof CardTitle
>) {
  let title = resource.title;
  if (resource.templateType === "advisory") {
    title = `[${resource?.advisoryId || resource?.cveId}] ${resource.title}`;
  }
  const plainTitle = title.replace(/`/g, "");
  const isCarousel = viewMode === "carousel";
  const titleContent =
    resource.templateType === "advisory" ? (
      <MarkdownInline content={title} />
    ) : (
      title
    );
  return (
    <CardTitle
      as="h3"
      title={plainTitle}
      clamp={!isCarousel}
      className={cn(
        "!font-medium subpixel-antialiased",
        {
          // Two lines at the active size, so selecting a slide can't grow the card.
          "min-w-0 overflow-hidden transition-[font-size] duration-300 !h-[calc(2*1.3*1.25rem)] md:!h-[calc(2*1.3*26px)]":
            isCarousel,
          "light:!text-black": !isCarousel,
        },
        className
      )}
      style={{
        gridArea: "title",
        ...style,
      }}
      data-tina-field={tinaField(resource, "title")}
      {...props}
    >
      {isCarousel ? (
        <span
          className="min-w-0"
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            lineHeight: 1.3,
            maxHeight: "2.6em",
          }}
        >
          {titleContent}
        </span>
      ) : (
        titleContent
      )}
    </CardTitle>
  );
}

function Description({
  resource,
  viewMode,
  style,
  ...props
}: {
  resource: any;
  active: boolean;
  viewMode: string;
} & React.ComponentProps<any>) {
  let description = resource.description;
  if (resource.templateType === "advisory") {
    description =
      resource?.description?.replaceAll("**", "").replace(/\n/g, " ") || "";
  }
  if (viewMode === "carousel") {
    return (
      <div
        className="mt-1 mb-6 w-full min-w-0 overflow-hidden self-start h-[calc(3*1.8*1rem)] md:h-[calc(3*1.8*17px)]"
        style={{ gridArea: "desc", ...style }}
      >
        <CardDescription
          clamp={false}
          className="line-clamp-3 !text-base md:!text-[17px] !leading-[1.8]"
          data-tina-field={tinaField(resource, "description")}
          {...props}
        >
          {description}
        </CardDescription>
      </div>
    );
  }
  return (
    <CardDescription
      className={cn("mb-6 !line-clamp-4 !min-h-0 overflow-hidden self-start", {
        "!line-clamp-2 mb-3": viewMode === "list",
      })}
      style={{ gridArea: "desc", ...style }}
      data-tina-field={tinaField(resource, "description")}
      {...props}
    >
      {description}
    </CardDescription>
  );
}

const COMPACT_TAXONOMY_MAX_CHARS = 24;

function formatTaxonomyLabel(title: string, compact?: boolean) {
  if (!compact || title.length <= COMPACT_TAXONOMY_MAX_CHARS) return title;
  return `${title.slice(0, COMPACT_TAXONOMY_MAX_CHARS).trimEnd()}…`;
}

function Categories({
  resource,
  compact = false,
  reserveStack = false,
  className,
  ...props
}: {
  resource: any;
  compact?: boolean;
  reserveStack?: boolean;
} & React.ComponentProps<any>) {
  const categoryItems = resource?.categories || [];
  const tagItems = resource?.tags || [];

  return (
    <div
      className={cn(
        "flex items-center flex-wrap gap-2 min-w-0 overflow-hidden",
        compact && "pr-2",
        reserveStack && "h-16 content-end",
        className
      )}
      style={{ gridArea: "categories" }}
      {...props}
    >
      {categoryItems.map(({ category }, index) =>
        category?.title ? (
          <TaxonomyBadge
            key={`category-${index}`}
            data-tina-field={tinaField(resource, `categories.[${index}]`)}
            variant="outline"
            title={category.title}
            className={compact ? "max-w-[13.25rem]" : undefined}
          >
            {formatTaxonomyLabel(category.title, compact)}
          </TaxonomyBadge>
        ) : null
      )}
      {tagItems.map(({ tag }, index) =>
        tag?.title ? (
          <TaxonomyBadge
            key={`tag-${index}`}
            data-tina-field={tinaField(resource, `tags.[${index}]`)}
            variant="outline"
            title={tag.title}
            className={compact ? "max-w-[18rem]" : undefined}
          >
            {formatTaxonomyLabel(tag.title, compact)}
          </TaxonomyBadge>
        ) : null
      )}
    </div>
  );
}

function CTA({
  resource,
  ctaLabel,
  compact = false,
  ...props
}: {
  resource: any;
  ctaLabel?: string;
  compact?: boolean;
} & React.ComponentProps<any>) {
  return (
    <div
      className="flex justify-end items-end shrink-0"
      style={{ gridArea: "cta" }}
      {...props}
    >
      <Button
        variant="link-glow"
        className={cn(
          "!opacity-100 touch-manipulation !min-h-11 !h-auto !px-3 !py-2 -my-1 text-white/80 hover:text-white group-hover:text-white",
          compact ? "-mr-3" : "-mx-3"
        )}
      >
        {ctaLabel}
        {resource?.title ? (
          <span className="sr-only"> — {resource.title}</span>
        ) : null}
        {resource.externalUrl && !resource.hasDetailPage && (
          <ArrowUpRightIcon className="!size-6 stroke-[1px]" />
        )}
      </Button>
    </div>
  );
}

export function Pagination({
  currentPage,
  totalPages,
  totalResults,
  onPageChange,
  pageSize = 30,
}: {
  currentPage: number;
  totalPages: number;
  totalResults: number;
  onPageChange: (page: number) => Promise<URLSearchParams>;
  pageSize?: number;
}) {
  const startResult = (currentPage - 1) * pageSize + 1;
  const endResult = Math.min(currentPage * pageSize, totalResults);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
      // window.scrollTo({ top: 0 });
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
      // window.scrollTo({ top: 0 });
    }
  };

  // Generate pagination items dynamically - always shows exactly 5 page numbers to avoid layout shift
  const generatePaginationItems = (
    currentPage: number,
    totalPages: number
  ): (number | "ellipsis")[] => {
    // If 5 or fewer pages, show all
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always show exactly 5 page numbers to prevent layout shift
    const items: (number | "ellipsis")[] = [];

    // Show first 5 pages when at the start
    if (currentPage <= 3) {
      items.push(1, 2, 3, 4, 5, "ellipsis", totalPages);
    }
    // Show last 5 pages when at the end
    else if (currentPage >= totalPages - 2) {
      items.push(
        1,
        "ellipsis",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    }
    // Show current page in the middle with 2 pages on each side
    else {
      items.push(
        1,
        "ellipsis",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "ellipsis",
        totalPages
      );
    }

    return items;
  };

  // Mobile simplified pagination - shows at least 3 page numbers
  const generateMobilePaginationItems = (
    currentPage: number,
    totalPages: number
  ): (number | "ellipsis")[] => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const items: (number | "ellipsis")[] = [];

    // When on first or second page, show [1, 2, ..., last]
    if (currentPage <= 2) {
      items.push(1, 2);
      if (totalPages > 3) {
        items.push("ellipsis");
      }
      items.push(totalPages);
    }
    // When on last or second-to-last page, show [1, ..., last-1, last]
    else if (currentPage >= totalPages - 1) {
      items.push(1);
      if (totalPages > 3) {
        items.push("ellipsis");
      }
      items.push(totalPages - 1, totalPages);
    }
    // When in the middle, show [1, ..., current, ..., last]
    else {
      items.push(1, "ellipsis", currentPage, "ellipsis", totalPages);
    }

    return items;
  };

  const paginationItems = generatePaginationItems(currentPage, totalPages);
  const mobilePaginationItems = generateMobilePaginationItems(
    currentPage,
    totalPages
  );

  return (
    <div className="items-center justify-between grid grid-cols-[1fr_2fr_1fr] max-md:grid-cols-1 gap-6 lg:gap-0">
      <div>
        <div className="font-mono flex justify-center md:justify-start max-sm:hidden">
          <span className="text-secondary-400 mr-2">
            {String(currentPage).padStart(2, "0")}
          </span>
          / {String(totalPages).padStart(2, "0")}
        </div>
      </div>
      <PaginationWrapper>
        {/* Desktop Pagination */}
        <PaginationContent className="hidden md:flex">
          <PaginationItem>
            <PaginationPrevious
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePrevious();
              }}
              aria-disabled={currentPage === 1}
              className={cn({
                "pointer-events-none opacity-50": currentPage === 1,
              })}
            />
          </PaginationItem>

          {paginationItems.map((item, index) => (
            <PaginationItem key={index}>
              {item === "ellipsis" ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  href="#"
                  className="!border-none hover:bg-white/10"
                  isActive={currentPage === item}
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(item);
                  }}
                >
                  {item}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          <PaginationItem>
            <PaginationNext
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleNext();
              }}
              aria-disabled={currentPage === totalPages}
              className={cn({
                "pointer-events-none opacity-50": currentPage === totalPages,
              })}
            />
          </PaginationItem>
        </PaginationContent>

        {/* Mobile Pagination - Simplified */}
        <PaginationContent className="flex md:hidden">
          <PaginationItem>
            <PaginationPrevious
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePrevious();
              }}
              aria-disabled={currentPage === 1}
              className={cn({
                "pointer-events-none opacity-50": currentPage === 1,
              })}
            />
          </PaginationItem>

          {mobilePaginationItems.map((item, index) => (
            <PaginationItem key={index}>
              {item === "ellipsis" ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  href="#"
                  className="!border-none hover:bg-white/10"
                  isActive={currentPage === item}
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(item);
                  }}
                >
                  {item}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          <PaginationItem>
            <PaginationNext
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleNext();
              }}
              aria-disabled={currentPage === totalPages}
              className={cn({
                "pointer-events-none opacity-50": currentPage === totalPages,
              })}
            />
          </PaginationItem>
        </PaginationContent>
      </PaginationWrapper>
      <div className="text-right text-sm flex items-center justify-center md:justify-end gap-4">
        <div className="text-sm text-muted-foreground-light">
          Showing{" "}
          <span className="font-medium text-white mx-0.5">
            {startResult}-{endResult}
          </span>{" "}
          of{" "}
          <span className="font-medium text-white mx-0.5">{totalResults}</span>{" "}
          results
        </div>
      </div>
    </div>
  );
}
